import Image from 'next/image';
import { SquareChevronLeft, SquareChevronRight } from 'lucide-react';
import Card from './card';
import Container from '@/components/Container';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip } from 'antd';
import { InfoCircleFilled, QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'next-i18next';
import { imgUrl, proxyImgUrl } from '@/shared/utils';

function tranfromNumber(number?: number) {
  const num = Number(number);
  if (!num) return 0;
  if (num < 1000) return num.toLocaleString();
  if (num < 1000_000) return `${Math.floor(num / 100) / 10}K`;
  if (num < 1000_000_000) return `${Math.floor(num / 100_000) / 10}M`;
  return `${Math.floor(num / 100_000_000) / 10}B`;
}

export default function SearchResult({ data }: { data?: any }) {
  const { task, suggest_ban, search_ban, ghost_ban_list } = data || {};
  const totalPage = Math.ceil(Number(ghost_ban_list?.length) / 4);
  const [currentPage, setCurrent] = useState(1);
  const { t } = useTranslation();

  const currentList = useMemo(() => {
    return (ghost_ban_list || []).slice((currentPage - 1) * 4, currentPage * 4);
  }, [currentPage, ghost_ban_list]);

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrent(currentPage - 1);
    }
  };
  const handleNext = () => {
    if (currentPage < totalPage) {
      setCurrent(currentPage + 1);
    }
  };

  return (
    <section className="max-w-[1200px] mt-[100px] mx-auto px-4">
      <div className="flex max-md:flex-col md:items-center justify-between px-2 py-9 border-b border-[#D9D9D9]">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 max-md:w-12 max-md:h-12 rounded-full overflow-hidden border border-[#D9D9D9]">
            {task?.image && (
              <img
                className="h-full w-full object-cover"
                src={imgUrl(proxyImgUrl(task?.image), 'mid')}
                width={78}
                height={78}
                alt=""
              />
            )}
          </div>
          <div>
            <div className="text-[#1D2129] font-bold text-[18px] max-md:text-sm">{task?.name}</div>
            <div className="flex items-center justify-between mt-2">
              <Image
                className="w-5 h-5 max-md:h-4 max-md:w-4"
                src="/images/instagram.svg"
                width={20}
                height={20}
                alt=""
              />
              <span className="mx-1 text-[#0C0C0C]/60 max-md:text-sm">{`@${task?.username}`}</span>
              {task?.is_verified && (
                <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.76941 1.28169C8.16467 0.858788 8.83533 0.858788 9.23059 1.28169L10.6294 2.77831C10.8265 2.98924 11.1052 3.10467 11.3937 3.09492L13.4411 3.02573C14.0196 3.00618 14.4938 3.48041 14.4743 4.05894L14.4051 6.10628C14.3953 6.39483 14.5108 6.67351 14.7217 6.87065L16.2183 8.26941C16.6412 8.66467 16.6412 9.33533 16.2183 9.73059L14.7217 11.1294C14.5108 11.3265 14.3953 11.6052 14.4051 11.8937L14.4743 13.9411C14.4938 14.5196 14.0196 14.9938 13.4411 14.9743L11.3937 14.9051C11.1052 14.8953 10.8265 15.0108 10.6294 15.2217L9.23059 16.7183C8.83533 17.1412 8.16467 17.1412 7.76941 16.7183L6.37065 15.2217C6.17351 15.0108 5.89483 14.8953 5.60628 14.9051L3.55894 14.9743C2.98041 14.9938 2.50618 14.5196 2.52573 13.9411L2.59492 11.8937C2.60467 11.6052 2.48924 11.3265 2.27831 11.1294L0.781695 9.73059C0.358788 9.33533 0.358788 8.66467 0.781694 8.26941L2.27831 6.87065C2.48924 6.67351 2.60467 6.39483 2.59492 6.10628L2.52573 4.05894C2.50618 3.48041 2.98041 3.00618 3.55894 3.02573L5.60628 3.09492C5.89483 3.10467 6.17351 2.98924 6.37065 2.77831L7.76941 1.28169Z"
                    fill="#3F92FF"
                  />
                  <path d="M5 9L7.5 11.5L12 7" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6 max-md:mt-4 max-md:justify-between">
          <div className="font-semibold text-2xl max-md:text-[13px] text-[#1D2129]">
            {tranfromNumber(task?.post_count)}
            <span className="text-[#0C0C0C99] text-[13px] font-normal ml-1">{t('Posts')}</span>
          </div>
          <div className="font-semibold text-2xl max-md:text-[13px] text-[#1D2129]">
            {tranfromNumber(task?.follower_count)}
            <span className="text-[#0C0C0C99] text-[13px] font-normal ml-1">{t('Followers')}</span>
          </div>
          <div className="font-semibold text-2xl max-md:text-[13px] text-[#1D2129]">
            {tranfromNumber(task?.following_count)}
            <span className="text-[#0C0C0C99] text-[13px] font-normal ml-1">{t('Following')}</span>
          </div>
        </div>
      </div>

      <div className="pt-9">
        <h2 className="font-semibold text-xl max-md:text-[18px]">{t('Shadowban Score')}</h2>
        <div className="flex items-center pt-8 text-[36px] font-semibold">
          <span className={task?.risk === 'LOW' ? 'text-[#1B9D42]' : 'text-[#EA373A]'}>{task?.risk}</span>
        </div>
      </div>
      <div className="pt-14">
        <h2 className="font-semibold text-xl">{t('Search Ban')}</h2>
        <div className="flex md:items-center gap-6 max-md:gap-3 pt-8">
          <div className="border border-[#D9D9D9] rounded-xl px-6 max-md:px-4 py-2 max-md:py-1 flex-1 flex flex-col">
            <div className="flex md:items-center max-md:flex-col flex-1">
              <span className="mr-3 text-[32px]">👁</span>
              <span className="text-[#1D2129] max-md:text-sm flex-1">{t('Search Suggestion Ban')}</span>
            </div>
            <div
              className={cn(
                'ml-11 max-md:ml-0 font-semibold',
                suggest_ban === 'LOW' ? 'text-[#1B9D42] text-[32px]' : 'text-[#EA373A] text-[32px]'
              )}
            >
              {suggest_ban}
            </div>
          </div>
          <div className="border border-[#D9D9D9] rounded-xl px-6 max-md:px-4  py-2 max-md:py-1 flex-1 flex flex-col">
            <div className="flex md:items-center max-md:flex-col flex-1">
              <span className="mr-3 text-[32px]">🔍</span>
              <span className="text-[#1D2129] max-md:text-sm flex-1">{t('Search Ban')}</span>
            </div>
            <div
              className={cn(
                'ml-11 max-md:ml-0 font-semibold',
                search_ban === 'LOW' ? 'text-[#1B9D42] text-[32px]' : 'text-[#EA373A] text-[32px]'
              )}
            >
              {search_ban}
            </div>
          </div>
        </div>
      </div>

      <div className="pt-14">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-xl flex items-center">
            {t('Ghost Ban')}
            {Number(ghost_ban_list?.length) > 4 && (
              <span className="ml-1">
                ({currentPage}/{totalPage})
              </span>
            )}
            <Tooltip
              placement="top"
              title={t(
                'Ghost Ban:Also known as a conventional shadowban, this happens when your posts are hidden from others while everything looks normal on your side. It often includes a search ban, making your content harder to find. Common triggers include excessive posting or following, and the restriction is usually temporary.'
              )}
            >
              <QuestionCircleFilled className="ml-1 text-[18px]" />
            </Tooltip>
          </h2>
          <div className="flex items-center gap-1.5 text-[#E5E5E5] max-md:hidden">
            <SquareChevronLeft
              size={26}
              className="hover:text-[#02183A] transition-colors cursor-pointer"
              onClick={handlePrev}
            />
            <SquareChevronRight
              size={26}
              className="hover:text-[#02183A] transition-colors cursor-pointer"
              onClick={handleNext}
            />
          </div>
        </div>
        <Container
          isFeedback={!ghost_ban_list?.length}
          feedback={
            <div className="mt-8 border border-[#D9D9D9] py-[70px] rounded-[12px] flex items-center justify-center text-[#1D2129] px-4">
              <InfoCircleFilled className="mr-2 text-[#FF4646]" />
              {t(
                'No posts found for this account. Ghost ban checks can only run when the account has at least one published post.'
              )}
            </div>
          }
        >
          <div className="grid grid-cols-2 max-md:grid-cols-1 gap-6 max-md:gap-3 mt-8">
            {currentList?.map((item: any) => (
              <Card key={item.postId} data={item} />
            ))}
          </div>
        </Container>
        <div className="flex justify-center gap-2 mt-4 md:hidden">
          <button
            className="flex-1 border border-[#D9D9D9] rounded-[6px] h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePrev}
            disabled={currentPage === 1}
          >
            {t('Previous')}
          </button>
          <button
            className="flex-1 border border-[#D9D9D9] rounded-[6px] h-11 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleNext}
            disabled={currentPage === totalPage}
          >
            {t('Next')}
          </button>
        </div>
      </div>
    </section>
  );
}
